// The read-aloud queue (WEB-7123): progress callouts while a reply runs, then the answer, one
// clip at a time. Callouts are only said when nothing else is, at most one every few seconds,
// and any not yet playing are dropped once the answer starts; they never cut into the answer.
//
// prepare(text, kind) starts getting a clip ready (Polly fetches it; the browser voice has
// nothing to fetch) and returns { ready: Promise, play(): Promise, cancel() }.

const PROGRESS_GAP_MS = 4000
// Answer clips fetched ahead of the one playing.
const PREFETCH = 2

export const createSpeechQueue = ({ prepare, onSpeaking = () => {}, now = () => Date.now(), progressGapMs = PROGRESS_GAP_MS }) => {
  let items = []
  let current = null
  let answerStarted = false
  let lastProgressAt = -Infinity
  let generation = 0
  let speaking = false

  const setSpeaking = on => { if (on !== speaking) { speaking = on; onSpeaking(on) } }
  const ensure = item => { if (!item.clip) item.clip = prepare(item.text, item.kind) }
  const prefetch = () => items.slice(0, PREFETCH).forEach(ensure)

  const pump = async () => {
    if (current) return
    const item = items.shift()
    if (!item) { setSpeaking(false); return }
    const gen = generation
    current = item
    ensure(item)
    prefetch()
    setSpeaking(true)
    try {
      await item.clip.ready
      if (gen !== generation || item.dropped) return
      item.playing = true
      await item.clip.play()
    } catch (e) {
      // A clip that can't be said is skipped.
    }
    if (gen !== generation) return
    current = null
    pump()
  }

  const drop = item => { item.dropped = true; if (item.clip) item.clip.cancel() }

  return {
    // Says a progress line now, or not at all.
    progress(text) {
      if (!text || answerStarted || current || items.length || now() - lastProgressAt < progressGapMs) return false
      lastProgressAt = now()
      items.push({ kind: 'progress', text })
      pump()
      return true
    },
    // Queues a piece of the answer.
    answer(text) {
      if (!text) return
      if (!answerStarted) {
        answerStarted = true
        items.filter(i => i.kind === 'progress').forEach(drop)
        items = items.filter(i => i.kind !== 'progress')
        // A callout still being fetched is dropped too; one already playing finishes.
        if (current && current.kind === 'progress' && !current.playing) {
          drop(current)
          current = null
        }
      }
      items.push({ kind: 'answer', text })
      if (current) prefetch()
      else pump()
    },
    // Stops speaking and forgets everything, ready for the next reply.
    stop() {
      generation++
      if (current) drop(current)
      items.forEach(drop)
      items = []
      current = null
      answerStarted = false
      lastProgressAt = -Infinity
      setSpeaking(false)
    },
    get speaking() { return speaking }
  }
}
